import React from "react";
import { Icon } from "Components/atoms/Icon";
import { useCalendarContext } from "shared/contexts/CalendarContext";
import {
  ContentDay,
  Day,
  GridWrapper,
  Header,
  IconDestination,
  IconOrigin,
} from "./styles";
import { DateGridProps } from "./types";

/**
 * Componente DateGrid
 *
 * Um calendário interativo exibido em formato de grade, com suporte a seleção de datas e marcadores visuais.
 */
export const DateGrid: React.FC<DateGridProps> = ({
  days,
  currentDate,
  onDatesChange,
  selectedDates,
  onSelectDate,
  grayDates,
  type,
  rangeStart,
  rangeEnd,
  continuousRange = false,
}) => {
  const { selectedOriginDates } = useCalendarContext();
  const today = new Date();
  const todayDate = today.getDate();
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();

  const weekDays = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SAB"];

  const maxOriginDateUTC = selectedOriginDates.length
    ? Math.max(
        ...selectedOriginDates.map((d) => {
          const date = new Date(d);
          return Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
          );
        }),
      )
    : null;

  const handleSelectDate = (day: number) => {
    const selectedDateObj = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
      0,
      0,
      0,
      0,
    );

    const selectedDate = `${selectedDateObj.getFullYear()}-${String(
      selectedDateObj.getMonth() + 1,
    ).padStart(2, "0")}-${String(selectedDateObj.getDate()).padStart(2, "0")}`;

    const isSelected = selectedDates.includes(selectedDate);
    const updatedDates = isSelected
      ? selectedDates.filter((date) => date !== selectedDate)
      : [...selectedDates, selectedDate];

    onDatesChange(updatedDates);
    onSelectDate(day);
  };

  return (
    <GridWrapper
      role="grid"
      aria-label="Calendar dates"
      $continuousRange={continuousRange}
    >
      {weekDays.map((day) => (
        <Header key={day} $continuousRange={continuousRange}>
          {day}
        </Header>
      ))}

      {days.map(
        (
          { day, isCurrentMonth, isSelectedOrigin, isSelectedDestination },
          index,
        ) => {
          if (day === null) {
            return (
              <Day
                key={index}
                isCurrentMonth={false}
                isSelected={false}
                isDisabled={true}
                aria-hidden="true"
              />
            );
          }

          const isDisabled =
            isCurrentMonth &&
            currentDate.getMonth() === todayMonth &&
            currentDate.getFullYear() === todayYear &&
            day < todayDate;

          const currentDateToCheckUTC = new Date(
            Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), day),
          ).getTime();

          const isReturnDisabled = Boolean(
            type === "destination" &&
              maxOriginDateUTC !== null &&
              currentDateToCheckUTC < maxOriginDateUTC,
          );

          let displayMonth = currentDate.getMonth();
          let displayYear = currentDate.getFullYear();

          if (!isCurrentMonth) {
            if (index < 7 && day > 15) {
              displayMonth -= 1;
              if (displayMonth < 0) {
                displayMonth = 11;
                displayYear -= 1;
              }
            } else if (index > 20 && day < 15) {
              displayMonth += 1;
              if (displayMonth > 11) {
                displayMonth = 0;
                displayYear += 1;
              }
            }
          }

          const monthString = String(displayMonth + 1).padStart(2, "0");
          const dayString = String(day).padStart(2, "0");
          const formattedDate = `${displayYear}-${monthString}-${dayString}`;

          const isGray = grayDates?.includes(formattedDate);
          const isSelected = selectedDates.includes(formattedDate);
          const isInRange = Boolean(
            rangeStart &&
              rangeEnd &&
              formattedDate > rangeStart &&
              formattedDate < rangeEnd,
          );
          const isRangeStart = Boolean(
            rangeStart && rangeEnd && formattedDate === rangeStart,
          );
          const isRangeEnd = Boolean(
            rangeStart && rangeEnd && formattedDate === rangeEnd,
          );
          const isRangeMember = isInRange || isRangeStart || isRangeEnd;
          const isOutsideCurrentMonth = displayMonth !== currentDate.getMonth();

          return (
            <Day
              key={index}
              aria-selected={isSelected}
              tabIndex={
                !isDisabled && !isReturnDisabled && !isOutsideCurrentMonth
                  ? 0
                  : -1
              }
              isSelected={isSelected}
              aria-disabled={
                isDisabled ||
                isReturnDisabled ||
                isOutsideCurrentMonth ||
                isGray
              }
              isSelectedDestination={isSelectedDestination}
              isSelectedOrigin={isSelectedOrigin}
              isCurrentMonth={isCurrentMonth || false}
              onClick={() =>
                !isDisabled &&
                !isReturnDisabled &&
                !isOutsideCurrentMonth &&
                handleSelectDate(day!)
              }
              isDisabled={
                isDisabled ||
                isReturnDisabled ||
                isOutsideCurrentMonth ||
                isGray
              }
              isGray={isGray}
              isInRange={isInRange}
              isRangeMember={isRangeMember}
              isRangeStart={isRangeStart}
              isRangeEnd={isRangeEnd}
              isRangeRowStart={index % 7 === 0}
              isRangeRowEnd={index % 7 === 6}
              $continuousRange={continuousRange}
            >
              <ContentDay>
                {isSelectedDestination && (isSelected || isGray) && (
                  <IconDestination isGray={isGray && !isSelected}>
                    <Icon
                      name="GoArrowDownLeft"
                      size="extraSmall"
                      color="black"
                      library="go"
                    />
                  </IconDestination>
                )}
                {isSelectedOrigin && (isSelected || isGray) && (
                  <IconOrigin isGray={isGray && !isSelected}>
                    <Icon
                      name="GoArrowUpRight"
                      size="extraSmall"
                      color="black"
                      library="go"
                    />
                  </IconOrigin>
                )}
                <p>{day}</p>
              </ContentDay>
            </Day>
          );
        },
      )}
    </GridWrapper>
  );
};
